import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../../components/layout/PageHeader";
import { useUnsavedChanges } from "../../../components/layout/UnsavedChanges";
import { useToast } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import {
  BRAND_PLANS_PATH,
  BRAND_SERVICES_PATH,
  ROUTES,
  enquiryDetailPath,
} from "../../../routes/paths";
// Deep imports rather than the brand barrel: that barrel pulls in the whole
// My brand screen, and this page only needs to know whether the workspace has
// said what it sells and what it does, and how to say so if it has not.
import PlansRequiredModal from "../../brand/components/PlansRequiredModal";
import ServicesRequiredModal from "../../brand/components/ServicesRequiredModal";
import { usePlansCreated } from "../../brand/hooks/usePlansCreated";
import AddEnquiryForm from "../components/AddEnquiryForm";
import { toCreateEnquiryPayload } from "../enquiryPayload";
import { useEnquiryLookups } from "../hooks/useEnquiryLookups";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { createEnquiry } from "../services/enquiriesService";
import { NewEnquiry } from "../types/enquiries.types";
import { validateNewEnquiry } from "../validation";

const EMPTY_ENQUIRY: NewEnquiry = {
  title: "",
  firstName: "",
  lastName: "",
  gender: "",
  dob: "",
  profession: "",
  lifestyle: "",
  goals: [],
  enquiryFrom: "",
  address: "",
  state: "",
  city: "",
  pincode: "",
  phone: "",
  whatsapp: "",
  whatsappSameAsPhone: false,
  email: "",
};

const AddEnquiryPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const getToken = useTokenGetter();
  const [enquiry, setEnquiry] = useState<NewEnquiry>(EMPTY_ENQUIRY);
  // Held so the action bar can say it is working and refuse a second press —
  // a double-submitted form is two enquiries.
  const [saving, setSaving] = useState(false);

  // Title, Goal and Life style are the backend's lists rather than ours, so
  // they are fetched as the page opens; Gender, Profession, Enquiry from,
  // State and City are fixed sets the app carries itself.
  const lookups = useEnquiryLookups();

  // Nothing to sell, so nothing for this lead to be a lead on — the payment
  // that comes next in the pipeline is requested against a plan. The list
  // screen turns its New enquiry buttons into the same prompt, so this is the
  // door somebody reaches by typing the URL, by a bookmark, or by coming back
  // to a tab left open since before the last plan was retired.
  //
  // The one bit rather than the whole list: unlike the Add client form, nothing
  // on this screen picks a plan, so there is no list to read the answer off.
  // `status === "ready"` is what keeps the prompt off a page whose read is
  // merely still out — see `useSetupGate` for why that caution matters.
  const { plansCreated, status: plansStatus } = usePlansCreated();
  const noPlans = plansStatus === "ready" && !plansCreated;

  // The other half of the same rule: the Goal field picks from the practice's
  // own active services, so with none listed it opens on an empty list and this
  // form cannot be finished either. Read off the lookups already in hand —
  // `serviceGoals` is that list, and it is the active ones alone.
  const goals = lookups.serviceGoals;
  const noServices = goals.status === "ready" && goals.options.length === 0;

  const errors = validateNewEnquiry(enquiry);
  const valid = Object.keys(errors).length === 0;

  // The form's own way of marking up what is still missing, so the rail's
  // prompt can offer to save a form that cannot be saved yet and point at why.
  const problems = useRef<(() => void) | null>(null);

  // Anything at all typed into the form is something a navigation would throw
  // away — there is no draft behind this screen, so the empty form it opened
  // with is the whole of what counts as nothing to lose.
  useUnsavedChanges(
    JSON.stringify(enquiry) !== JSON.stringify(EMPTY_ENQUIRY),
    {
      title: "Unsaved enquiry details",
      body: "You’ve entered details for this enquiry but haven’t saved them yet. Save your changes, or they’ll be discarded.",
      confirmLabel: "Save & add",
    },
    // The Save row's own answer, handed to the prompt so its button does what
    // it says. `commit` is called through an arrow rather than passed: it is
    // declared below, and the literal is built as this renders.
    {
      canSave: valid && !saving,
      save: async () => (await commit()) !== null,
      report: () => problems.current?.(),
    }
  );

  const handleChange = (patch: Partial<NewEnquiry>) =>
    setEnquiry((current) => ({ ...current, ...patch }));

  /**
   * Adds the enquiry and answers with where it now lives, or `null` where
   * nothing was stored.
   *
   * The path rather than a bare yes, because this is the one of these forms
   * whose destination is only known once the record exists. Going there is left
   * to whoever asked: the Save row opens the new enquiry, the rail's prompt
   * carries on to wherever it was pointing. It must not navigate itself, or the
   * prompt's answer would take somebody somewhere they did not ask for.
   */
  const commit = async (): Promise<string | null> => {
    if (saving) return null;

    // The dropdowns hold names; the endpoint takes the ids behind them. A name
    // with no id left to match means a catalog list changed while this form
    // sat open — worth naming the field rather than posting a broken body.
    const result = toCreateEnquiryPayload(enquiry, lookups);
    if (!result.ok) {
      toast.error(
        "Some options are out of date",
        `Pick ${result.unresolved.join(" and ")} again.`
      );
      return null;
    }

    setSaving(true);

    let record;
    try {
      record = await createEnquiry(getToken, result.payload);
    } catch (error) {
      toast.error(
        "Could not add this enquiry",
        error instanceof ApiError
          ? error.message
          : "We could not reach the server. Check your connection and try again."
      );
      console.error("adding an enquiry failed:", error);
      // Left on the form with everything still in it, which is the only place
      // the details can be corrected and sent again.
      setSaving(false);
      return null;
    }

    // Raised before the navigation, and it outlives it: the toast provider
    // sits above the router, so the message is still standing when the next
    // screen paints.
    const name = `${enquiry.firstName} ${enquiry.lastName}`.trim();
    toast.success("Enquiry added", `${name} is now on your enquiries list.`);

    // No `setSaving(false)`: the page is leaving either way, and setting state
    // on the way out is a warning about updating a component that has gone.
    return enquiryDetailPath(record.id);
  };

  const handleSave = async () => {
    const added = await commit();
    if (added) navigate(added);
  };

  return (
    // No bottom padding on purpose: the action bar sits flush on the foot of
    // the shell, and the form supplies the space under its own buttons.
    <div className="min-h-full flex flex-col px-page pt-page-top">
      <PageHeader
        title="New enquiry"
        breadcrumbs={[
          { label: "Enquiries", to: ROUTES.enquiries },
          { label: "New enquiry" },
        ]}
      />

      <AddEnquiryForm
        enquiry={enquiry}
        onChange={handleChange}
        errors={errors}
        lookups={lookups}
        saving={saving}
        onSave={handleSave}
        onCancel={() => navigate(ROUTES.enquiries)}
        problems={problems}
      />

      {/* The way out goes back to the list rather than closing onto the form,
          which is the difference between this and the same prompt on the list
          screen: there, dismissing leaves them on a screen that still works.

          The plan is asked for first where both are missing — the order the My
          brand tabs hold them, and the order the list screen asks in. */}
      {noPlans && (
        <PlansRequiredModal
          onCreatePlan={() => navigate(BRAND_PLANS_PATH)}
          onDismiss={() => navigate(ROUTES.enquiries)}
          dismissLabel="Back to enquiries"
        />
      )}

      {!noPlans && noServices && (
        <ServicesRequiredModal
          onCreateService={() => navigate(BRAND_SERVICES_PATH)}
          onDismiss={() => navigate(ROUTES.enquiries)}
          dismissLabel="Back to enquiries"
        />
      )}
    </div>
  );
};

export default AddEnquiryPage;
